import { useState, useEffect, useRef } from "react";

const BASE = "https://support.gs-group.fr/api";
const token = () => localStorage.getItem("nexo_token");
const headers = () => ({ "Content-Type":"application/json", Authorization:`Bearer ${token()}` });

export default function ModalPV({ T, intervention, onClose, onSaved }) {
  const [modeles, setModeles] = useState([]);
  const [selectedModele, setSelectedModele] = useState(null);
  const [rapport, setRapport] = useState(null);
  const [donnees, setDonnees] = useState({});
  const [photos, setPhotos] = useState([]);
  const [sigTech, setSigTech] = useState("");
  const [sigClient, setSigClient] = useState("");
  const [step, setStep] = useState("choose");
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const sigTechRef = useRef(null);
  const sigClientRef = useRef(null);

  // ── Signature client distante ──
  const [pvStatus,      setPvStatus]      = useState(null);
  const [dateInstall,   setDateInstall]   = useState("");
  const [savingDate,    setSavingDate]    = useState(false);
  const [sendingLink,   setSendingLink]   = useState(false);
  const [sigToast,      setSigToast]      = useState("");

  const showSigToast = (msg) => { setSigToast(msg); setTimeout(() => setSigToast(""), 3500); };

  const loadPvStatus = () => {
    fetch(`${BASE}/interventions/${intervention.id}/pv-status`, { headers: headers() })
      .then(r => r.ok ? r.json() : {})
      .then(d => {
        setPvStatus(d);
        if (d.date_installation) setDateInstall(d.date_installation.split("T")[0]);
      })
      .catch(() => {});
  };

  const saveDate = async () => {
    if (!dateInstall) return;
    setSavingDate(true);
    try {
      const r = await fetch(`${BASE}/interventions/${intervention.id}/date-installation`, {
        method: "PATCH", headers: headers(),
        body: JSON.stringify({ date_installation: dateInstall }),
      });
      if (r.ok) { showSigToast("✅ Date d'installation enregistrée"); loadPvStatus(); }
      else { let d={}; try{d=await r.json();}catch{}; showSigToast("❌ " + (d.error||"Erreur")); }
    } catch { showSigToast("❌ Erreur réseau"); }
    setSavingDate(false);
  };

  const sendLink = async () => {
    setSendingLink(true);
    try {
      const r = await fetch(`${BASE}/interventions/${intervention.id}/send-signature-link`, {
        method: "POST", headers: headers(),
      });
      let d = {}; try { d = await r.json(); } catch {}
      if (!r.ok) {
        showSigToast("❌ " + (d.error || `Erreur ${r.status}`));
      } else if (d.emailOk === false) {
        if (d.signUrl) {
          try { await navigator.clipboard.writeText(d.signUrl); } catch {}
          showSigToast("⚠️ SMTP non configuré — lien copié dans le presse-papier");
        } else {
          showSigToast("⚠️ Email non envoyé : " + (d.emailError || "erreur SMTP"));
        }
      } else {
        showSigToast("📧 Lien de signature envoyé au client !");
        loadPvStatus();
      }
    } catch { showSigToast("❌ Impossible de joindre le serveur"); }
    setSendingLink(false);
  };

  useEffect(() => {
    // Charger modèles
    fetch(`${BASE}/modeles`, { headers: headers() }).then(r=>r.json()).then(data => {
      if (Array.isArray(data)) setModeles(data);
    });
    // Charger rapport existant
    fetch(`${BASE}/modeles/rapport/${intervention.id}`, { headers: headers() }).then(r=>r.json()).then(data => {
      if (data) {
        setRapport(data);
        setDonnees(data.donnees||{});
        setPhotos(data.photos_urls||[]);
        setSigTech(data.signature_technicien||"");
        setSigClient(data.signature_client||"");
        setSelectedModele({ id: data.modele_id, nom: data.modele_nom, sections: data.sections, type: data.modele_type });
        setStep("fill");
      }
    });
    // Charger statut signature
    loadPvStatus();

    // Pré-remplir avec données client
    setDonnees(prev => ({
      ...prev,
      raison_sociale: intervention.client_nom || "",
      adresse_siege: intervention.client_adresse ? `${intervention.client_adresse}, ${intervention.client_ville||""} ${intervention.client_cp||""}`.trim() : "",
      adresse_site: intervention.client_adresse ? `${intervention.client_adresse}, ${intervention.client_ville||""} ${intervention.client_cp||""}`.trim() : "",
      date_intervention: intervention.date_planifiee ? new Date(intervention.date_planifiee).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      technicien: intervention.technicien_nom || "",
    }));
  }, []);

  const upD = (key, val) => setDonnees(p => ({...p, [key]: val}));
  const upTable = (tableId, rowIdx, colIdx, val) => {
    setDonnees(p => {
      const table = [...(p[tableId] || [])];
      if (!table[rowIdx]) table[rowIdx] = [];
      table[rowIdx][colIdx] = val;
      return { ...p, [tableId]: table };
    });
  };

  const addPhoto = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => setPhotos(p => [...p, { url: ev.target.result, name: file.name, date: new Date().toLocaleString('fr-FR') }]);
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (idx) => setPhotos(p => p.filter((_,i)=>i!==idx));

  const save = async (valide=false) => {
    setSaving(true);
    try {
      await fetch(`${BASE}/modeles/rapport`, {
        method: "POST", headers: headers(),
        body: JSON.stringify({
          intervention_id: intervention.id,
          modele_id: selectedModele.id,
          donnees, photos_urls: photos,
          signature_technicien: sigTech,
          signature_client: sigClient,
          valide
        })
      });
      onSaved && onSaved();
      if (valide) onClose();
    } catch(e) {}
    setSaving(false);
  };

  const inp = (key, placeholder="", type="text") => (
    <input value={donnees[key]||""} onChange={e=>upD(key,e.target.value)} placeholder={placeholder} type={type}
      style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:8,padding:"9px 12px",color:T.text,fontSize:13,outline:"none",width:"100%"}}/>
  );
  const textarea = (key, placeholder="", rows=3) => (
    <textarea value={donnees[key]||""} onChange={e=>upD(key,e.target.value)} placeholder={placeholder} rows={rows}
      style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:8,padding:"9px 12px",color:T.text,fontSize:13,outline:"none",width:"100%",resize:"vertical",lineHeight:1.5}}/>
  );

  const renderSection = (section) => {
    switch(section.type) {
      case "info": return (
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {section.champs.map(champ => (
            <div key={champ.id} style={{display:"flex",flexDirection:"column",gap:5}}>
              <label style={{fontSize:11,fontWeight:700,color:T.mutedL,textTransform:"uppercase",letterSpacing:"0.5px"}}>
                {champ.label}{champ.requis?" *":""}
              </label>
              {champ.type==="textarea" ? textarea(champ.id, champ.label) :
               champ.type==="radio" ? (
                <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                  {champ.options.map(opt=>(
                    <label key={opt} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13,color:T.text,padding:"7px 12px",borderRadius:8,border:`1.5px solid ${donnees[champ.id]===opt?T.accent:T.border}`,background:donnees[champ.id]===opt?T.accentDim:"transparent"}}>
                      <input type="radio" checked={donnees[champ.id]===opt} onChange={()=>upD(champ.id,opt)} style={{accentColor:T.accent}}/>
                      {opt}
                    </label>
                  ))}
                </div>
               ) :
               champ.type==="checkbox" ? (
                <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
                  <input type="checkbox" checked={!!donnees[champ.id]} onChange={e=>upD(champ.id,e.target.checked)}
                    style={{width:16,height:16,accentColor:T.accent}}/>
                  <span style={{fontSize:13,color:T.text}}>{donnees[champ.id]?"Oui":"Non"}</span>
                </label>
               ) : inp(champ.id, champ.label, champ.type)}
            </div>
          ))}
        </div>
      );

      case "tableau": return (
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr>{section.colonnes.map(col=>(
                <th key={col} style={{padding:"8px 10px",background:T.bg,border:`1px solid ${T.border}`,fontSize:11,fontWeight:700,color:T.muted,textAlign:"left",letterSpacing:"0.5px",textTransform:"uppercase"}}>{col}</th>
              ))}</tr>
            </thead>
            <tbody>
              {Array.from({length:section.lignes}).map((_,ri)=>(
                <tr key={ri}>
                  {section.colonnes.map((col,ci)=>(
                    <td key={ci} style={{border:`1px solid ${T.border}22`,padding:4}}>
                      <input value={(donnees[section.id]?.[ri]?.[ci])||""} onChange={e=>upTable(section.id,ri,ci,e.target.value)}
                        style={{background:"transparent",border:"none",outline:"none",color:T.text,fontSize:12,width:"100%",padding:"5px 7px"}}/>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

      case "tableau_cameras": return (
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr>{section.colonnes.map(col=>(
                <th key={col} style={{padding:"8px 10px",background:T.bg,border:`1px solid ${T.border}`,fontSize:11,fontWeight:700,color:T.muted,textAlign:"left"}}>{col}</th>
              ))}</tr>
            </thead>
            <tbody>
              {Array.from({length:section.lignes}).map((_,ri)=>(
                <tr key={ri}>
                  <td style={{border:`1px solid ${T.border}22`,padding:"6px 10px",fontSize:12,fontWeight:700,color:T.muted,background:T.bg,whiteSpace:"nowrap"}}>
                    CAM {String(ri+1).padStart(2,"0")}
                  </td>
                  {section.colonnes.slice(1).map((col,ci)=>(
                    <td key={ci} style={{border:`1px solid ${T.border}22`,padding:4}}>
                      <input value={(donnees[section.id]?.[ri]?.[ci])||""} onChange={e=>upTable(section.id,ri,ci,e.target.value)}
                        style={{background:"transparent",border:"none",outline:"none",color:T.text,fontSize:12,width:"100%",padding:"5px 7px"}}/>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

      case "formation": return (
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {section.champs.map(champ=>(
            <div key={champ.id} style={{padding:"12px 14px",background:T.bg,borderRadius:9,border:`1px solid ${T.border}`}}>
              <div style={{fontSize:13,fontWeight:600,color:T.text,marginBottom:8}}>{champ.label}</div>
              {champ.type==="checkbox" ? (
                <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
                  <input type="checkbox" checked={!!donnees[champ.id]} onChange={e=>upD(champ.id,e.target.checked)} style={{width:16,height:16,accentColor:T.accent}}/>
                  <span style={{fontSize:13,color:donnees[champ.id]?T.success:T.muted}}>{donnees[champ.id]?"✅ Oui":"☐ Non"}</span>
                </label>
              ) : champ.type==="radio" ? (
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {champ.options.map(opt=>(
                    <label key={opt} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13,color:T.text,padding:"6px 12px",borderRadius:8,border:`1.5px solid ${donnees[champ.id]===opt?T.accent:T.border}`,background:donnees[champ.id]===opt?T.accentDim:"transparent"}}>
                      <input type="radio" checked={donnees[champ.id]===opt} onChange={()=>upD(champ.id,opt)} style={{accentColor:T.accent}}/>
                      {opt}
                    </label>
                  ))}
                </div>
              ) : inp(champ.id)}
            </div>
          ))}
        </div>
      );

      case "photos": return (
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <label style={{display:"flex",alignItems:"center",gap:10,padding:"16px 20px",background:T.bg,borderRadius:11,border:`2px dashed ${T.border}`,cursor:"pointer",justifyContent:"center"}}>
            <input type="file" multiple accept="image/*" onChange={addPhoto} style={{display:"none"}}/>
            <span style={{fontSize:24}}>📷</span>
            <div style={{textAlign:"center"}}>
              <div style={{fontSize:13,fontWeight:600,color:T.text}}>Ajouter des photos</div>
              <div style={{fontSize:11,color:T.muted,marginTop:2}}>Cliquez ou glissez vos photos ici · JPG, PNG</div>
            </div>
          </label>
          {photos.length>0 && (
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
              {photos.map((p,i)=>(
                <div key={i} style={{position:"relative",borderRadius:10,overflow:"hidden",border:`1px solid ${T.border}`,aspectRatio:"4/3",background:T.bg}}>
                  <img src={p.url} alt={p.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                  <div style={{position:"absolute",bottom:0,left:0,right:0,background:"rgba(0,0,0,0.6)",padding:"5px 8px",fontSize:10,color:"#fff"}}>
                    {p.name} · {p.date}
                  </div>
                  <button onClick={()=>removePhoto(i)} style={{position:"absolute",top:5,right:5,background:"rgba(239,68,68,0.9)",border:"none",borderRadius:"50%",width:22,height:22,color:"#fff",cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
                </div>
              ))}
            </div>
          )}
          {photos.length===0 && <div style={{textAlign:"center",fontSize:12,color:T.muted}}>Aucune photo ajoutée</div>}
        </div>
      );

      case "signatures": return (
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          {section.champs.map(champ=>(
            <div key={champ.id} style={{display:"flex",flexDirection:"column",gap:5}}>
              <label style={{fontSize:11,fontWeight:700,color:T.mutedL,textTransform:"uppercase",letterSpacing:"0.5px"}}>{champ.label}</label>
              {inp(champ.id)}
            </div>
          ))}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginTop:8}}>
            {[["✍️ Signature technicien",sigTech,setSigTech],["🖊️ Signature client / cachet",sigClient,setSigClient]].map(([label,val,setter])=>(
              <div key={label} style={{display:"flex",flexDirection:"column",gap:8}}>
                <div style={{fontSize:12,fontWeight:700,color:T.text}}>{label}</div>
                <div style={{background:T.bg,border:`2px dashed ${val?T.success:T.border}`,borderRadius:10,height:120,display:"flex",alignItems:"center",justifyContent:"center",position:"relative",overflow:"hidden"}}>
                  {val ? (
                    <div style={{textAlign:"center"}}>
                      <div style={{fontSize:13,color:T.success,fontWeight:700}}>✅ Signé</div>
                      <div style={{fontFamily:"cursive",fontSize:22,color:T.text,marginTop:4}}>{val}</div>
                      <button onClick={()=>setter("")} style={{marginTop:6,padding:"3px 10px",borderRadius:6,border:`1px solid ${T.danger}`,background:"transparent",color:T.danger,fontSize:11,cursor:"pointer"}}>Effacer</button>
                    </div>
                  ) : (
                    <div style={{width:"100%",padding:"0 12px"}}>
                      <input placeholder="Saisir nom complet pour signature" onChange={e=>setter(e.target.value)}
                        style={{background:"transparent",border:"none",borderBottom:`1px solid ${T.border}`,outline:"none",color:T.text,fontSize:13,width:"100%",padding:"8px 0",textAlign:"center"}}/>
                      <div style={{fontSize:10,color:T.muted,textAlign:"center",marginTop:6}}>Saisir le nom pour valider la signature</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div style={{padding:"12px 14px",background:`${T.success}10`,border:`1px solid ${T.success}30`,borderRadius:9,fontSize:12,color:T.mutedL}}>
            ℹ️ La signature numérique est valable conformément aux CGV GS-GROUP · {new Date().toLocaleDateString('fr-FR')}
          </div>
        </div>
      );

      default: return <div style={{color:T.muted,fontSize:13}}>Section non reconnue</div>;
    }
  };

  // ÉTAPE 1 — Choisir le modèle
  if (step==="choose") return (
    <div style={{position:"fixed",inset:0,background:"rgba(8,12,30,0.22)",backdropFilter:"blur(4px)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:30,width:540}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
          <div>
            <div style={{fontSize:18,fontWeight:800,color:T.text}}>📋 Modèle d'intervention</div>
            <div style={{fontSize:12,color:T.muted,marginTop:3}}>Choisissez le type de PV à remplir</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:20}}>✕</button>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {modeles.map(m=>{
            const icons = { installation_camera:"📷", installation_alarme:"🚨" };
            const colors = { installation_camera:T.accent, installation_alarme:T.danger };
            const c = colors[m.type]||T.accent;
            return (
              <div key={m.id} onClick={()=>{ setSelectedModele(m); setStep("fill"); }}
                style={{display:"flex",alignItems:"center",gap:14,padding:"16px 18px",borderRadius:13,border:`1.5px solid ${T.border}`,cursor:"pointer",transition:"all 0.15s",background:T.bg}}
                onMouseEnter={e=>e.currentTarget.style.borderColor=c}
                onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>
                <div style={{width:48,height:48,borderRadius:12,background:`${c}22`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,flexShrink:0}}>
                  {icons[m.type]||"📋"}
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:15,fontWeight:800,color:T.text}}>{m.nom}</div>
                  <div style={{fontSize:12,color:T.muted,marginTop:3}}>{m.description}</div>
                </div>
                <span style={{fontSize:20,color:T.muted}}>→</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // ÉTAPE 2 — Remplir le formulaire
  const sections = selectedModele?.sections || [];
  const currentSection = sections[activeSection];

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(8,12,30,0.22)",backdropFilter:"blur(4px)",zIndex:1000,display:"flex",flexDirection:"column",overflow:"hidden"}}>
      {/* Header */}
      <div style={{background:T.sidebar,borderBottom:`1px solid ${T.border}`,padding:"14px 24px",display:"flex",alignItems:"center",gap:14,flexShrink:0}}>
        <div style={{width:36,height:36,borderRadius:9,background:`linear-gradient(135deg,${T.accent},${T.purple})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>📋</div>
        <div style={{flex:1}}>
          <div style={{fontSize:15,fontWeight:800,color:T.text}}>{selectedModele?.nom} — {intervention.titre}</div>
          <div style={{fontSize:11,color:T.muted}}>Client : {intervention.client_nom||"—"} · {intervention.client_ville||""}</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={()=>save(false)} disabled={saving} style={{padding:"8px 15px",borderRadius:9,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,fontSize:13,fontWeight:600,cursor:"pointer"}}>
            💾 Sauvegarder
          </button>
          <button onClick={()=>save(true)} disabled={saving||!sigTech||!sigClient} style={{padding:"8px 15px",borderRadius:9,background:sigTech&&sigClient?T.success:T.border,border:"none",color:"#fff",fontSize:13,fontWeight:700,cursor:sigTech&&sigClient?"pointer":"not-allowed",opacity:saving?0.7:1}}>
            ✓ Valider & Clôturer
          </button>
          <button onClick={onClose} style={{padding:"8px 12px",borderRadius:9,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,fontSize:18,cursor:"pointer",lineHeight:1}}>✕</button>
        </div>
      </div>

      {/* Toast signature */}
      {sigToast && (
        <div style={{position:"fixed",top:24,right:24,zIndex:9999,background:"#1e293b",color:"#fff",padding:"11px 20px",borderRadius:12,fontSize:13,fontWeight:600,boxShadow:"0 8px 24px #00000044",pointerEvents:"none"}}>
          {sigToast}
        </div>
      )}

      {/* Barre signature client */}
      <div style={{background:T.bg,borderBottom:`1px solid ${T.border}`,padding:"10px 24px",display:"flex",alignItems:"center",gap:16,flexShrink:0,flexWrap:"wrap"}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:12,fontWeight:700,color:T.muted,whiteSpace:"nowrap"}}>📅 Date d'installation :</span>
          <input
            type="date"
            value={dateInstall}
            max={new Date().toISOString().split('T')[0]}
            onChange={e => setDateInstall(e.target.value)}
            disabled={!!pvStatus?.signature_client_at}
            style={{padding:"5px 9px",borderRadius:8,border:`1px solid ${T.border}`,background:T.card,color:T.text,fontSize:12,outline:"none",cursor:pvStatus?.signature_client_at?"not-allowed":"auto"}}
          />
          {!pvStatus?.signature_client_at && (
            <button onClick={saveDate} disabled={savingDate||!dateInstall}
              style={{padding:"5px 12px",borderRadius:8,background:"#0369a1",border:"none",color:"#fff",fontSize:11,fontWeight:700,cursor:savingDate||!dateInstall?"not-allowed":"pointer",opacity:(savingDate||!dateInstall)?0.7:1}}>
              {savingDate ? "…" : "💾 Enregistrer"}
            </button>
          )}
        </div>

        <div style={{height:24,width:1,background:T.border,flexShrink:0}}/>

        {pvStatus?.signature_client_at ? (
          <span style={{display:"inline-flex",alignItems:"center",gap:7,fontSize:12,fontWeight:700,color:"#065f46",background:"#d1fae5",border:"1px solid #6ee7b7",padding:"5px 13px",borderRadius:20}}>
            ✅ Signé le {new Date(pvStatus.signature_client_at).toLocaleDateString("fr-FR")}
            {pvStatus.signature_client_nom && ` par ${pvStatus.signature_client_nom}`}
          </span>
        ) : (
          <button onClick={sendLink} disabled={sendingLink}
            style={{padding:"6px 14px",borderRadius:9,background:"#e0f2fe",border:"1px solid #7dd3fc",color:"#075985",fontSize:12,fontWeight:700,cursor:sendingLink?"not-allowed":"pointer",opacity:sendingLink?0.7:1,whiteSpace:"nowrap"}}>
            {sendingLink ? "Envoi en cours…" : "📧 Envoyer lien de signature au client"}
          </button>
        )}
      </div>

      <div style={{display:"flex",flex:1,overflow:"hidden"}}>
        {/* Sidebar sections */}
        <div style={{width:220,background:T.sidebar,borderRight:`1px solid ${T.border}`,overflowY:"auto",flexShrink:0,padding:"12px 8px"}}>
          {sections.map((s,i)=>{
            const active=activeSection===i;
            const icons = {info:"ℹ️",tableau:"📊",tableau_cameras:"📷",formation:"🎓",photos:"📸",signatures:"✍️"};
            return (
              <button key={s.id} onClick={()=>setActiveSection(i)}
                style={{display:"flex",alignItems:"center",gap:9,width:"100%",padding:"9px 11px",borderRadius:9,background:active?T.accentDim:"transparent",border:"none",color:active?T.accent:T.muted,fontSize:12,fontWeight:active?700:500,cursor:"pointer",textAlign:"left",marginBottom:2}}>
                <span style={{fontSize:14}}>{icons[s.type]||"📋"}</span>
                <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.titre}</span>
              </button>
            );
          })}
        </div>

        {/* Contenu section */}
        <div style={{flex:1,overflowY:"auto",padding:24}}>
          {currentSection && (
            <div style={{maxWidth:800,margin:"0 auto"}}>
              <div style={{marginBottom:20}}>
                <div style={{fontSize:19,fontWeight:800,color:T.text,marginBottom:4}}>{currentSection.titre}</div>
                <div style={{height:2,background:`linear-gradient(90deg,${T.accent},transparent)`,borderRadius:2,width:120}}/>
              </div>
              <div style={{background:T.card,borderRadius:14,border:`1px solid ${T.border}`,padding:22}}>
                {renderSection(currentSection)}
              </div>
              <div style={{display:"flex",justifyContent:"space-between",marginTop:16}}>
                <button onClick={()=>setActiveSection(Math.max(0,activeSection-1))} disabled={activeSection===0}
                  style={{padding:"9px 16px",borderRadius:9,border:`1px solid ${T.border}`,background:"transparent",color:activeSection===0?T.border:T.muted,fontSize:13,fontWeight:600,cursor:activeSection===0?"not-allowed":"pointer"}}>
                  ← Section précédente
                </button>
                <span style={{fontSize:12,color:T.muted,alignSelf:"center"}}>{activeSection+1} / {sections.length}</span>
                <button onClick={()=>setActiveSection(Math.min(sections.length-1,activeSection+1))} disabled={activeSection===sections.length-1}
                  style={{padding:"9px 16px",borderRadius:9,border:`1px solid ${T.accent}`,background:T.accentDim,color:T.accent,fontSize:13,fontWeight:600,cursor:activeSection===sections.length-1?"not-allowed":"pointer"}}>
                  Section suivante →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
